'use client';

import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import Link from 'next/link';
import { StateMark } from '@/components/ui/HorizonMark';
import {
  TrendingUp,
  TrendingDown,
  Search,
  ArrowUpDown,
  Star,
  Coins,
  ImageIcon,
  LayoutGrid,
  Megaphone,
  Share2
} from 'lucide-react';
import TokenImage from '@/components/ui/TokenImage';
import { NFTImage } from '@/components/ui';
import { TokenSparklineCompact } from '@/components/market/TokenSparkline';
import { useFirestoreCrypto, resolveLensTokens, AbstractToken } from '@/hooks/useFirestoreCrypto';
import { useTokenMetadata } from '@/hooks/useTokenMetadata';
import { useFirestoreCollectionStats } from '@/hooks/useFirestoreCollectionStats';
import { useFirestoreCollections } from '@/hooks/useFirestoreCollections';
import { useMultiplePriceHistory } from '@/hooks/usePriceHistory';
import { getTimeAgo } from '@/hooks/useMarketAnalysis';
import { entityUrl } from '@/lib/entity-url';
import { getCollectionCurrency, getCollectionBlockchain } from '@/utils/blockchain';
import { getChainLabel } from '@/lib/chains';
import { useAdManagement } from '@/hooks/useAdManagement';
import { useAssetGlossaryStatus } from '@/hooks/useAssetGlossaryStatus';

type AssetTab = 'tokens' | 'nfts';
type SortField = 'name' | 'price' | 'change' | 'volume' | 'marketcap';
type SortDirection = 'asc' | 'desc';

export interface UnifiedAsset {
  id: string;
  type: 'token' | 'nft';
  chain?: string; // chain id ('abstract' | 'base' | 'ethereum' | …) — drives the cross-chain badge
  name: string;
  symbol?: string;
  slug?: string;
  image?: string;
  price: number;
  priceFormatted: string;
  change24h: number;
  hasChangeData: boolean;
  volume24h: number;
  volumeUsd: number; // Volume in USD for consistent sorting (NFT volumes converted from ETH/RON)
  volumeFormatted: string;
  marketCap?: number;
  marketCapFormatted?: string;
  sales?: number;
  currency: string;
  featured?: boolean;
  tradingUrl?: string;
  address?: string;
  hasGlossaryPage?: boolean; // Whether this asset has an entity glossary page
  /** The glossary tag's slug — the direct dossier door (`/tags/<slug>/`). */
  glossarySlug?: string;
  // OpenSea metadata (NFT collections only)
  isVerified?: boolean;
  description?: string;
  // Additional OpenSea stats (NFT collections)
  numOwners?: number;
  totalSupply?: number;
  averagePrice?: number;
  sevenDayVolume?: number;
  sevenDayChange?: number;
  thirtyDayVolume?: number;
  thirtyDayChange?: number;
  // Social links from OpenSea
  externalUrl?: string;
  twitterUsername?: string;
  discordUrl?: string;
  instagramUsername?: string;
  // Contract info
  contracts?: Array<{ address: string; chain: string }>;
  // Created date
  createdDate?: string;
}

/**
 * The desk's produced read on one asset (market-v2 "◆ desk read" affordance).
 * Built by the caller from `market_analysis/insights` / `collection_analysis/insights`;
 * keyed by `UnifiedAsset.id`. `fresh` (<12h) gates the gold ◆ + inline expansion;
 * `entitySlug` (when the insight names its entity) turns the asset name into a
 * dossier door.
 */
export interface AssetDeskRead {
  analysis: string;
  timestamp: string;
  fresh: boolean;
  chips: string[];
  entitySlug?: string;
  entityName?: string;
}

/**
 * Story-tick: this asset's entity appeared in the desk's latest brief or on a
 * fresh wire item — rendered as a subtle gold left tick with a named source.
 */
export interface AssetStoryTick {
  kind: 'brief' | 'wire';
  label: string;
  href: string;
}

interface UnifiedAssetDirectoryProps {
  className?: string;
  initialTab?: AssetTab;
  onTabChange?: (tab: AssetTab) => void;
  /** Enable select mode - clicking selects instead of navigating */
  selectMode?: boolean;
  /** Selected asset ID in select mode */
  selectedAssetId?: string | null;
  /** Callback when asset is selected in select mode */
  onAssetSelect?: (asset: UnifiedAsset | null) => void;
  /** Called when assets are loaded with the first asset (for auto-selection) */
  onFirstAssetReady?: (asset: UnifiedAsset) => void;
  /** Callback when share button is clicked (for market overview share) */
  onShareClick?: () => void;
  /**
   * Restrict the directory to a single chain (the "chain lens"). Tokens come from the
   * Abstract-only price feed today, so a non-Abstract chain yields no tokens (NFTs are
   * filtered by `getCollectionBlockchain`). Omit for the cross-chain "All chains" view.
   */
  chainId?: string;
  /**
   * Per-asset desk reads keyed by `UnifiedAsset.id` (market-v2 ◆ affordance).
   * Omit (standalone /market) → zero behavior change.
   */
  deskReads?: Record<string, AssetDeskRead>;
  /** Story-ticks keyed by `UnifiedAsset.id`. Omit → zero behavior change. */
  storyTicks?: Record<string, AssetStoryTick>;
}

export default function UnifiedAssetDirectory({
  className = '',
  initialTab = 'tokens',
  onTabChange,
  selectMode = false,
  selectedAssetId = null,
  onAssetSelect,
  onFirstAssetReady,
  onShareClick,
  chainId,
  deskReads,
  storyTicks
}: UnifiedAssetDirectoryProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  
  const urlTab = searchParams.get('tab') as AssetTab | null;
  const [activeTab, setActiveTab] = useState<AssetTab>(urlTab || initialTab);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);
  // market-v2 ◆ affordance: which asset's desk read is expanded inline under its row.
  const [expandedReadId, setExpandedReadId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('marketcap');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Width-based column gating. This table renders in contexts of very different widths
  // (full-bleed standalone /market vs the narrow dashboard col-span-8, where the sidebar
  // leaves only ~480px). Viewport breakpoints misfire there — just above lg the dashboard
  // table crams all six columns and the Vol value prints over the 7D sparkline. So we gate
  // columns by the table's ACTUAL width, dropping the heaviest first (Mcap, then Vol) while
  // KEEPING the 7D sparkline — the CoinMarketCap / CoinGecko degrade order.
  const [tableW, setTableW] = useState<number | null>(null);
  const roRef = useRef<ResizeObserver | null>(null);
  // Callback ref (not useEffect+ref): the table wrapper mounts AFTER the loading skeleton,
  // so a one-shot mount effect would attach to a null node and never re-observe. A callback
  // ref attaches the observer whenever the node actually appears.
  const setTableWrap = useCallback((node: HTMLDivElement | null) => {
    if (roRef.current) {
      roRef.current.disconnect();
      roRef.current = null;
    }
    if (node && typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver((entries) => {
        const w = entries[0]?.contentRect.width;
        if (w) setTableW(Math.round(w));
      });
      ro.observe(node);
      roRef.current = ro;
    }
  }, []);

  // Column width map (% of table) per tier; a column shows iff it has an entry. A null
  // width (SSR / first paint) → the full set, matching the server render so there's no
  // hydration mismatch; the observer narrows it on mount.
  const colW = useMemo<Partial<Record<'asset' | 'price' | 'change' | 'spark' | 'vol' | 'mcap', number>>>(() => {
    const w = tableW ?? 9999;
    if (w >= 660) return { asset: 34, price: 15, change: 10, spark: 13, vol: 12, mcap: 16 };
    if (w >= 540) return { asset: 38, price: 18, change: 11, spark: 16, mcap: 17 };
    if (w >= 420) return { asset: 40, price: 23, change: 15, spark: 22 };
    return { asset: 48, price: 28, change: 24 };
  }, [tableW]);
  const showSpark = colW.spark !== undefined;
  const showVol = colW.vol !== undefined;
  const showMcap = colW.mcap !== undefined;

  const { data: cryptoData, loading: tokensLoading } = useFirestoreCrypto();
  const { tokenMetadata } = useTokenMetadata();

  const { allStats, loading: statsLoading } = useFirestoreCollectionStats({
    enableRealTimeUpdates: true,
  });
  const { collectionsData, loading: collectionsLoading } = useFirestoreCollections();
  
  // Fetch glossary status for all assets
  const tokenList = useMemo(() => 
    cryptoData?.abstract_tokens
      ?.filter(token => !['BTC', 'ETH', 'BITCOIN', 'ETHEREUM'].includes(token.symbol.toUpperCase()))
      .map(token => ({ symbol: token.symbol })) || [],
    [cryptoData?.abstract_tokens]
  );
  
  const nftList = useMemo(() => 
    Object.keys(allStats || {}).map(slug => ({ slug })),
    [allStats]
  );
  
  const { glossaryStatus, loading: glossaryLoading } = useAssetGlossaryStatus(tokenList, nftList);

  // Batch fetch price history for all tokens (7-day sparklines)
  const tokenSymbols = useMemo(() =>
    cryptoData?.abstract_tokens
      ?.filter(token => !['BTC', 'ETH', 'BITCOIN', 'ETHEREUM'].includes(token.symbol.toUpperCase()))
      .filter(token => token.currentPrice != null && token.currentPrice > 0)
      .map(token => token.symbol) || [],
    [cryptoData?.abstract_tokens]
  );
  const priceHistoryMap = useMultiplePriceHistory(tokenSymbols);

  const { getActiveAdsByLocation, trackImpression, trackClick } = useAdManagement();
  const sponsoredAds = useMemo(() => getActiveAdsByLocation('market-hub-sponsored'), [getActiveAdsByLocation]);

  const loading = tokensLoading || statsLoading || collectionsLoading;

  const formatVolume = useCallback((value: number) => {
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    if (value >= 1e3) return `$${(value / 1e3).toFixed(2)}K`;
    return `$${value?.toLocaleString() || '0'}`;
  }, []);

  const formatPrice = useCallback((price: number | null | undefined) => {
    if (price == null) {
      return '$0.00';
    }
    if (price >= 1) {
      return `$${price.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    } else if (price >= 0.01) {
      return `$${price.toFixed(4)}`;
    } else {
      return `$${price.toFixed(6)}`;
    }
  }, []);

  const formatFloorPrice = useCallback((price: number, currency: string) => {
    if (price === 0) return `0 ${currency}`;
    if (price >= 1) return `${price.toFixed(2)} ${currency}`;
    if (price >= 0.01) return `${price.toFixed(3)} ${currency}`;
    return `${price.toFixed(4)} ${currency}`;
  }, []);

  // Get ETH price for converting NFT volumes to USD
  const ethPriceUsd = cryptoData?.ethereum?.currentPrice || 3000; // fallback to ~$3000

  const tokenAssets: UnifiedAsset[] = useMemo(() => {
    // Lens-scoped tokens via the shared resolver (see resolveLensTokens): the per-chain
    // `tokensByChain[chainId]` feed, falling back to the legacy flat `abstract_tokens` for
    // Abstract and the cross-chain view. Replaces a hardcoded `chainId !== 'abstract' → []`
    // rule that made the token table permanently Abstract-only — the twin of the one in
    // MarketTerminal, which is why both now call the same helper.
    const lensTokens = resolveLensTokens(cryptoData, chainId);
    if (lensTokens.length === 0) return [];

    return lensTokens
      .filter(token => !['BTC', 'ETH', 'BITCOIN', 'ETHEREUM'].includes(token.symbol.toUpperCase()))
      // Filter out inactive tokens (no price data from DexScreener - likely delisted or no liquidity)
      .filter(token => token.currentPrice != null && token.currentPrice > 0)
      .map((token) => {
        const metadata = tokenMetadata.find(m => m.symbol === token.symbol);
        const volume = token.volume24h || 0;
        return {
          id: `token-${token.symbol}`,
          type: 'token' as const,
          // The asset's chain is the LENS's chain; the cross-chain view still shows the
          // Abstract feed, so it keeps 'abstract'. (Was hardcoded 'abstract' unconditionally.)
          chain: chainId ?? 'abstract',
          name: token.name,
          symbol: token.symbol,
          image: token.imageUrl || token.ogImage,
          price: token.currentPrice,
          priceFormatted: formatPrice(token.currentPrice),
          change24h: token.priceChange24h || 0,
          hasChangeData: token.priceChange24h !== undefined && token.priceChange24h !== null,
          volume24h: volume,
          volumeUsd: volume, // Already in USD
          volumeFormatted: formatVolume(volume),
          marketCap: token.marketCap,
          marketCapFormatted: token.marketCap ? formatVolume(token.marketCap) : undefined,
          currency: 'USD',
          featured: metadata?.featured || false,
          tradingUrl: token.tradingUrl,
          address: token.address,
          hasGlossaryPage: !!glossaryStatus[`token:${token.symbol}`],
          glossarySlug: glossaryStatus[`token:${token.symbol}`] || undefined,
        };
      });
  }, [cryptoData, tokenMetadata, formatPrice, formatVolume, glossaryStatus, chainId]);

  const nftAssets: UnifiedAsset[] = useMemo(() => {
    return Object.entries(allStats || {})
      // Chain lens: keep only collections on the requested chain (by `blockchain` config).
      .filter(([slug]) => !chainId || getCollectionBlockchain(slug) === chainId)
      .map(([slug, stats]) => {
      const metadata = collectionsData.find(c => c.slug === slug);
      const currency = getCollectionCurrency(slug);
      const floorPrice = stats.floorPrice || 0;
      const volume = stats.oneDayVolume || 0;
      
      // Convert to USD for consistent sorting:
      // - ETH collections: multiply by ETH price
      // - RON collections: we don't have RON price, so use 0 for sorting (still display native)
      const volumeUsd = currency === 'ETH' ? volume * ethPriceUsd : 0;

      return {
        id: `nft-${slug}`,
        type: 'nft' as const,
        chain: getCollectionBlockchain(slug),
        name: metadata?.name || slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        slug,
        // Image priority: admin upload > OpenSea image > fallback
        image: metadata?.featuredImage || metadata?.image || stats.imageUrl || undefined,
        price: floorPrice,
        priceFormatted: formatFloorPrice(floorPrice, currency),
        change24h: stats.oneDayChange || 0,
        hasChangeData: stats.oneDayChange !== undefined && stats.oneDayChange !== null && stats.oneDayChange !== 0,
        volume24h: volume,
        volumeUsd, // USD equivalent for sorting (0 for RON since we don't have price)
        volumeFormatted: `${volume.toFixed(2)} ${currency}`,
        sales: stats.totalSales,
        currency,
        featured: metadata?.featured || false,
        hasGlossaryPage: !!glossaryStatus[`nft:${slug}`],
        glossarySlug: glossaryStatus[`nft:${slug}`] || undefined,
        // OpenSea metadata
        isVerified: stats.isVerified || false,
        description: stats.description || undefined,
        // Additional OpenSea stats
        numOwners: stats.numOwners || undefined,
        totalSupply: stats.totalSupply || undefined,
        averagePrice: stats.averagePrice || undefined,
        sevenDayVolume: stats.sevenDayVolume || undefined,
        sevenDayChange: stats.sevenDayChange || undefined,
        thirtyDayVolume: stats.thirtyDayVolume || undefined,
        thirtyDayChange: stats.thirtyDayChange || undefined,
        // Social links from OpenSea
        externalUrl: stats.externalUrl || undefined,
        twitterUsername: stats.twitterUsername || undefined,
        discordUrl: stats.discordUrl || undefined,
        instagramUsername: stats.instagramUsername || undefined,
        // Contract info
        contracts: stats.contracts || undefined,
        // Created date
        createdDate: stats.createdDate || undefined,
      };
    });
  }, [allStats, collectionsData, formatFloorPrice, ethPriceUsd, glossaryStatus, chainId]);

  const filteredAssets = useMemo(() => {
    let assets: UnifiedAsset[] = [];

    if (activeTab === 'tokens') {
      assets = [...assets, ...tokenAssets];
    }
    if (activeTab === 'nfts') {
      assets = [...assets, ...nftAssets];
    }

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      assets = assets.filter(asset =>
        asset.name.toLowerCase().includes(search) ||
        asset.symbol?.toLowerCase().includes(search) ||
        asset.slug?.toLowerCase().includes(search)
      );
    }

    if (showFeaturedOnly) {
      assets = assets.filter(asset => asset.featured);
    }

    assets.sort((a, b) => {
      let aValue: number | string, bValue: number | string;

      switch (sortField) {
        case 'name':
          return sortDirection === 'asc'
            ? a.name.localeCompare(b.name)
            : b.name.localeCompare(a.name);
        case 'price':
          aValue = a.price;
          bValue = b.price;
          break;
        case 'change':
          aValue = a.change24h;
          bValue = b.change24h;
          break;
        case 'volume':
          // Use volumeUsd for consistent cross-currency sorting
          aValue = a.volumeUsd;
          bValue = b.volumeUsd;
          break;
        case 'marketcap':
          aValue = a.marketCap || a.sales || 0;
          bValue = b.marketCap || b.sales || 0;
          break;
        default:
          return 0;
      }

      return sortDirection === 'asc'
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    });

    return assets;
  }, [activeTab, tokenAssets, nftAssets, searchTerm, showFeaturedOnly, sortField, sortDirection]);

  React.useEffect(() => {
    if (sponsoredAds.length > 0 && filteredAssets.length > 2) {
      trackImpression(sponsoredAds[0].id);
    }
  }, [sponsoredAds, filteredAssets.length, trackImpression]);

  // Auto-select first asset when assets are loaded (for select mode)
  const hasCalledFirstAssetRef = React.useRef(false);
  React.useEffect(() => {
    if (
      selectMode &&
      onFirstAssetReady &&
      !hasCalledFirstAssetRef.current &&
      !loading &&
      filteredAssets.length > 0
    ) {
      hasCalledFirstAssetRef.current = true;
      onFirstAssetReady(filteredAssets[0]);
    }
  }, [selectMode, onFirstAssetReady, loading, filteredAssets]);

  // Honor ?asset= deep-link on initial load (select mode only).
  // Entity-graph orbit nodes link here as /market?tab=<tokens|nfts>&asset=<param>:
  //   tokens → param is the lowercased symbol; nfts → param is the collection slug.
  // Without this, the asset param was written by selection but never read back, so
  // deep-links (e.g. clicking an NFT asset node) landed on /market with nothing selected.
  const hasHandledAssetParamRef = React.useRef(false);
  React.useEffect(() => {
    if (!selectMode || !onAssetSelect || hasHandledAssetParamRef.current || loading) return;
    // Attempt exactly once, on the first render after data is ready.
    hasHandledAssetParamRef.current = true;
    const assetParam = searchParams.get('asset');
    if (!assetParam) return;
    const target = assetParam.toLowerCase();
    const match = [...tokenAssets, ...nftAssets].find(asset =>
      asset.type === 'token'
        ? asset.symbol?.toLowerCase() === target
        : asset.slug?.toLowerCase() === target
    );
    if (!match) return;
    // Align the tab with the matched asset so its row is visible/highlighted
    // (orbit nodes pass the correct tab, but guard the bare ?asset= case too).
    const matchTab: AssetTab = match.type === 'token' ? 'tokens' : 'nfts';
    if (matchTab !== activeTab) {
      setActiveTab(matchTab);
      onTabChange?.(matchTab);
    }
    onAssetSelect(match);
  }, [selectMode, onAssetSelect, loading, tokenAssets, nftAssets, searchParams, activeTab, onTabChange]);

  const handleTabChange = (tab: AssetTab) => {
    setActiveTab(tab);
    onTabChange?.(tab);
    const params = new URLSearchParams(searchParams.toString());
    if (tab === 'tokens') {
      params.delete('tab');
    } else {
      params.set('tab', tab);
    }
    const base = pathname || '/market';
    const newUrl = params.toString() ? `${base}?${params.toString()}` : base;
    router.replace(newUrl, { scroll: false });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown size={10} className="text-neutral-400 dark:text-neutral-600" />;
    return sortDirection === 'asc'
      ? <TrendingUp size={10} className="text-emerald-500" />
      : <TrendingDown size={10} className="text-emerald-500" />;
  };

  const tabs = [
    { id: 'tokens' as const, label: 'Tokens', icon: Coins, count: tokenAssets.length },
    { id: 'nfts' as const, label: 'NFTs', icon: ImageIcon, count: nftAssets.length },
  ];

  // Chain badge — only meaningful when the directory spans multiple chains (the
  // cross-chain "All chains" /market view). On a chain-scoped page every row is
  // already the same chain, so we suppress it. Type is conveyed by the tab + the
  // colored avatar dot, so we no longer render a redundant TKN/NFT tag.
  const ChainBadge = ({ chain }: { chain?: string }) => {
    const label = getChainLabel(chain);
    if (!label) return null;
    return (
      <span className="text-[8px] font-mono font-bold uppercase tracking-wide px-1 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 flex-shrink-0">
        {label}
      </span>
    );
  };
  // Cross-chain mode = no chain lens; only then does a per-row chain badge add info.
  const crossChain = !chainId;

  if (loading) {
    return (
      <div className={className}>
        <div className="animate-pulse space-y-0">
          <div className="flex gap-2 px-4 py-3 border-b border-neutral-200 dark:border-neutral-800">
            <div className="h-8 bg-neutral-200 dark:bg-neutral-800 rounded w-28"></div>
            <div className="h-8 bg-neutral-200 dark:bg-neutral-800 rounded w-20"></div>
            <div className="h-8 bg-neutral-200 dark:bg-neutral-800 rounded w-20"></div>
          </div>
          <div className="h-10 bg-neutral-100 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800"></div>
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-14 bg-neutral-50 dark:bg-neutral-950 border-b border-neutral-200/50 dark:border-neutral-800/50"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Sticky Header: Tabs + Controls */}
      <div className="sticky top-0 z-20 bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800">
        {/* Tab Navigation */}
        <div className="border-b border-neutral-200 dark:border-neutral-800">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-1.5">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-mono font-bold transition-all ${
                    activeTab === tab.id
                      ? 'bg-emerald-500 text-white dark:text-neutral-900'
                      : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-neutral-800'
                  }`}
                >
                  <tab.icon size={16} />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className={`font-mono text-xs px-2 py-0.5 rounded ${
                    activeTab === tab.id
                      ? 'bg-white/30 text-white dark:bg-neutral-900/30 dark:text-neutral-900'
                      : 'bg-neutral-200 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-500'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
            
            {/* Share Button */}
            {onShareClick && (
              <button
                onClick={onShareClick}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 transition-all bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-700 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 hover:border-emerald-400 dark:hover:border-emerald-600 text-emerald-700 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-300 "
                title={`Share ${activeTab === 'tokens' ? 'Token' : 'NFT'} Market Overview`}
              >
                <Share2 size={16} />
                <span className="text-sm font-mono font-bold">Share</span>
              </button>
            )}
          </div>
        </div>

        {/* Controls Bar */}
        <div className="py-3 px-4 bg-neutral-50/80 dark:bg-neutral-900/80 backdrop-blur-sm">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 dark:text-neutral-500" />
              <input
                type="text"
                placeholder="Search assets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white placeholder:text-neutral-400 dark:placeholder:text-neutral-500 border border-neutral-300 dark:border-neutral-700 rounded-lg text-sm font-mono focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>

            {/* Featured Filter */}
            <label className="flex items-center gap-2 px-4 py-2.5 bg-amber-100 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-800/30 rounded-lg cursor-pointer hover:bg-amber-200 dark:hover:bg-amber-900/30 transition-colors">
              <input
                type="checkbox"
                checked={showFeaturedOnly}
                onChange={(e) => setShowFeaturedOnly(e.target.checked)}
                className="rounded border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-emerald-500 focus:ring-emerald-500"
              />
              <span className="text-xs font-mono font-semibold text-amber-700 dark:text-amber-400 whitespace-nowrap uppercase">Featured</span>
            </label>

          {/* Results count */}
          <div className="hidden sm:flex items-center px-4 py-2.5 bg-neutral-100 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg">
            <span className="text-xs font-mono text-neutral-500 dark:text-neutral-400">
              <span className="font-bold text-neutral-900 dark:text-white">{filteredAssets.length}</span> assets
            </span>
          </div>
        </div>
        </div>
      </div>

      {/* Mobile Asset List */}
      <div className="block md:hidden">
        <div className="divide-y divide-neutral-200/50 dark:divide-neutral-800/50">
          {filteredAssets.map((asset) => {
            const isSelected = selectMode && selectedAssetId === asset.id;
            const read = deskReads?.[asset.id];
            const tick = storyTicks?.[asset.id];
            const handleClick = () => {
              if (selectMode && onAssetSelect) {
                onAssetSelect(isSelected ? null : asset);
              } else {
                const url = asset.type === 'token'
                  ? `/coins/${asset.symbol?.toLowerCase()}`
                  : `/collections/${asset.slug}`;
                window.location.href = url;
              }
            };

            return (
            <div
              key={asset.id}
              onClick={handleClick}
              className={`flex items-center gap-4 py-4 px-4 cursor-pointer transition-colors ${
                isSelected 
                  ? 'bg-emerald-100 dark:bg-emerald-900/20 border-l-2 border-emerald-500' 
                  : asset.featured
                    ? 'bg-amber-50 dark:bg-amber-900/10 border-l-2 border-amber-500 hover:bg-amber-100 dark:hover:bg-amber-900/20'
                    : 'hover:bg-neutral-100 dark:hover:bg-neutral-800/50'
              }`}
              // Story-tick (market-v2): gold left edge naming a real source.
              style={tick && !isSelected && !asset.featured ? { boxShadow: 'inset 2px 0 0 #E8A832' } : undefined}
              title={tick ? tick.label : undefined}
            >
              {/* Asset Image with Type Indicator */}
              <div className="relative w-10 h-10 flex-shrink-0">
                {asset.type === 'token' ? (
                  <TokenImage
                    symbol={asset.symbol || ''}
                    address={asset.address || ''}
                    name={asset.name}
                    imageUrl={asset.image}
                    size={40}
                    className="rounded-full"
                  />
                ) : asset.image ? (
                  <img
                    src={asset.image}
                    alt={asset.name}
                    className="w-full h-full object-cover rounded-lg"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-neutral-900 dark:text-white text-sm font-bold bg-neutral-200 dark:bg-neutral-700 rounded-lg">
                    {asset.name?.charAt(0) || '?'}
                  </div>
                )}
                {/* Type indicator dot */}
                <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white dark:border-neutral-900 ${
                  asset.type === 'token' ? 'bg-emerald-500' : 'bg-violet-500'
                }`} />
              </div>

              {/* Asset Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-sm text-neutral-900 dark:text-white truncate">
                    {asset.type === 'token' ? asset.symbol : asset.name}
                  </span>
                  {/* ◆ = fresh desk read (tap the row — the read rides the detail sheet). */}
                  {read?.fresh && (
                    <span className="flex-shrink-0 font-mono text-[10px] leading-none text-amber-500 dark:text-amber-400" title="Fresh desk read">
                      ◆
                    </span>
                  )}
                  {asset.featured && <Star size={12} className="text-amber-500 fill-current flex-shrink-0" />}
                </div>
                <div className="text-xs font-mono text-neutral-500 truncate">
                  {asset.type === 'token' ? asset.name : asset.slug}
                </div>
              </div>

              {/* Price & Stats */}
              <div className="text-right flex-shrink-0">
                <div className="font-mono font-bold text-sm text-neutral-900 dark:text-white">
                  {asset.priceFormatted}
                </div>
                <div className={`font-mono text-xs font-medium ${
                  !asset.hasChangeData ? 'text-neutral-400 dark:text-neutral-600' :
                  asset.change24h >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
                }`}>
                  {asset.hasChangeData 
                    ? `${asset.change24h >= 0 ? '↑' : '↓'}${Math.abs(asset.change24h).toFixed(2)}%`
                    : '—'
                  }
                </div>
              </div>
            </div>
            );
          })}
        </div>
      </div>

      {/* Desktop Table Layout */}
      <div ref={setTableWrap} className="hidden md:block overflow-x-auto scrollbar-hide">
        <table className="w-full table-fixed">
          <thead className="bg-neutral-100 dark:bg-neutral-800/50 border-b border-neutral-200 dark:border-neutral-800">
            <tr>
              <th style={{ width: `${colW.asset}%` }} className="px-4 py-3 text-left text-xs font-mono font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                Asset
              </th>
              <th
                style={{ width: `${colW.price}%` }}
                className="px-3 py-3 text-right text-xs font-mono font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-200 dark:hover:bg-neutral-800 transition-colors"
                onClick={() => handleSort('price')}
              >
                <div className="flex items-center justify-end gap-1">
                  <span>Price</span>
                  {getSortIcon('price')}
                </div>
              </th>
              <th
                style={{ width: `${colW.change}%` }}
                className="px-3 py-3 text-right text-xs font-mono font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-200 dark:hover:bg-neutral-800 transition-colors"
                onClick={() => handleSort('change')}
              >
                <div className="flex items-center justify-end gap-1">
                  <span>24h</span>
                  {getSortIcon('change')}
                </div>
              </th>
              {showSpark && (
                <th style={{ width: `${colW.spark}%` }} className="px-3 py-3 text-center text-xs font-mono font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  7D
                </th>
              )}
              {showVol && (
                <th
                  style={{ width: `${colW.vol}%` }}
                  className="px-3 py-3 text-right text-xs font-mono font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-200 dark:hover:bg-neutral-800 transition-colors"
                  onClick={() => handleSort('volume')}
                >
                  <div className="flex items-center justify-end gap-1">
                    <span title="24h Volume (sorted by USD equivalent)">Vol 24h</span>
                    {getSortIcon('volume')}
                  </div>
                </th>
              )}
              {showMcap && (
                <th
                  style={{ width: `${colW.mcap}%` }}
                  className="px-3 py-3 text-right text-xs font-mono font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-200 dark:hover:bg-neutral-800 transition-colors"
                  onClick={() => handleSort('marketcap')}
                >
                  <div className="flex items-center justify-end gap-1">
                    <span title={activeTab === 'nfts' ? 'Total Sales Count' : 'Market Cap'}>
                      {activeTab === 'nfts' ? 'Sales' : 'Mcap'}
                    </span>
                    {getSortIcon('marketcap')}
                  </div>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200/50 dark:divide-neutral-800/50 bg-white dark:bg-neutral-950">
            {filteredAssets.map((asset, index) => {
              const shouldShowAd = index === 2 && sponsoredAds.length > 0;
              const adToShow = shouldShowAd ? sponsoredAds[0] : null;
              // market-v2 desk affordances (absent props → all undefined, zero change).
              const read = deskReads?.[asset.id];
              const tick = storyTicks?.[asset.id];
              const isReadExpanded = !!read?.fresh && expandedReadId === asset.id;
              const visibleCols = 3 + (showSpark ? 1 : 0) + (showVol ? 1 : 0) + (showMcap ? 1 : 0);

              return (
                <React.Fragment key={asset.id}>
                  {/* Sponsored Ad Row */}
                  {shouldShowAd && adToShow && (
                    <tr 
                      className="bg-amber-50 dark:bg-amber-900/10 border-l-2 border-amber-500 hover:bg-amber-100 dark:hover:bg-amber-900/20 transition-colors cursor-pointer"
                      onClick={async () => {
                        await trackClick(adToShow.id);
                        if (adToShow.buttonUrl) {
                          window.open(adToShow.buttonUrl, '_blank', 'noopener,noreferrer');
                        }
                      }}
                    >
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-3">
                          <div className="relative w-8 h-8 flex-shrink-0">
                            {adToShow.imageUrl ? (
                              <img
                                src={adToShow.imageUrl}
                                alt={adToShow.title}
                                className="w-full h-full rounded object-cover"
                              />
                            ) : (
                              <div 
                                className="w-full h-full rounded flex items-center justify-center"
                                style={{ backgroundColor: adToShow.backgroundColor }}
                              >
                                <Megaphone size={14} className="text-white" />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 max-w-[280px]">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold text-xs text-neutral-900 dark:text-white truncate">{adToShow.title}</span>
                              <span className="px-1 py-0.5 bg-amber-200 dark:bg-amber-900/50 border border-amber-300 dark:border-amber-700/50 text-amber-800 dark:text-amber-400 text-[8px] font-mono font-semibold rounded flex-shrink-0">
                                SPONSORED
                              </span>
                            </div>
                            <div className="text-[10px] font-mono text-neutral-500 dark:text-neutral-500 truncate">{adToShow.description}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <span className="text-neutral-400 dark:text-neutral-600 font-mono text-xs">—</span>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <span className="text-neutral-400 dark:text-neutral-600 font-mono text-xs">—</span>
                      </td>
                      {showSpark && (
                        <td className="px-4 py-2.5 text-center">
                          <span className="text-neutral-400 dark:text-neutral-600 font-mono text-xs">—</span>
                        </td>
                      )}
                      {showVol && (
                        <td className="px-4 py-2.5 text-right">
                          <span className="text-neutral-400 dark:text-neutral-600 font-mono text-xs">—</span>
                        </td>
                      )}
                      {showMcap && (
                        <td className="px-4 py-2.5 text-right">
                          <span className="text-neutral-400 dark:text-neutral-600 font-mono text-xs">—</span>
                        </td>
                      )}
                    </tr>
                  )}

                  {/* Regular Asset Row */}
                  <tr
                    className={`cursor-pointer transition-colors ${
                      selectMode && selectedAssetId === asset.id
                        ? 'bg-emerald-100 dark:bg-emerald-900/20'
                        : asset.featured 
                          ? 'bg-amber-50 dark:bg-amber-900/10 hover:bg-amber-100 dark:hover:bg-amber-900/20' 
                          : 'hover:bg-neutral-100 dark:hover:bg-neutral-800/50'
                    }`}
                    onClick={() => {
                      if (selectMode && onAssetSelect) {
                        const isSelected = selectedAssetId === asset.id;
                        onAssetSelect(isSelected ? null : asset);
                      } else {
                        const url = asset.type === 'token'
                          ? `/coins/${asset.symbol?.toLowerCase()}`
                          : `/collections/${asset.slug}`;
                        window.location.href = url;
                      }
                    }}
                  >
                    <td
                      className="px-4 py-3"
                      // Story-tick: subtle gold left edge naming a real source (recap/wire).
                      style={tick ? { boxShadow: 'inset 2px 0 0 #E8A832' } : undefined}
                      title={tick ? tick.label : undefined}
                    >
                      <div className="flex items-center gap-3">
                        {/* Asset Image with Type Indicator */}
                        <div className="relative w-9 h-9 flex-shrink-0">
                          {asset.type === 'token' ? (
                            <TokenImage
                              symbol={asset.symbol || ''}
                              address={asset.address || ''}
                              name={asset.name}
                              imageUrl={asset.image}
                              size={36}
                              className="rounded-full"
                            />
                          ) : asset.image ? (
                            <img
                              src={asset.image}
                              alt={asset.name}
                              className="w-full h-full object-cover rounded-lg"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-neutral-900 dark:text-white text-sm font-bold bg-neutral-200 dark:bg-neutral-700 rounded-lg">
                              {asset.name?.charAt(0) || '?'}
                            </div>
                          )}
                          {/* Type indicator dot */}
                          <div 
                            className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-neutral-950 ${
                              asset.type === 'token' ? 'bg-emerald-500' : 'bg-violet-500'
                            }`}
                            title={asset.type === 'token' ? 'Token' : 'NFT Collection'}
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            {/* Name doors into the entity dossier when the desk read names one. */}
                            {read?.entitySlug ? (
                              <Link
                                href={entityUrl(read.entitySlug, { hash: 'market' })}
                                onClick={(e) => e.stopPropagation()}
                                title={`${read.entityName || asset.name} — entity dossier`}
                                className="font-mono font-bold text-sm text-neutral-900 dark:text-white truncate hover:underline underline-offset-2"
                              >
                                {asset.type === 'token' ? asset.symbol : asset.name}
                              </Link>
                            ) : (
                              <span className="font-mono font-bold text-sm text-neutral-900 dark:text-white truncate">
                                {asset.type === 'token' ? asset.symbol : asset.name}
                              </span>
                            )}
                            {crossChain && asset.type === 'nft' && <ChainBadge chain={asset.chain} />}
                            {asset.featured && <Star size={10} className="text-amber-500 fill-current flex-shrink-0" />}
                            {/* ◆ = the desk holds a FRESH read; expands inline under the row. */}
                            {read?.fresh && (
                              <button
                                type="button"
                                aria-expanded={isReadExpanded}
                                title={isReadExpanded ? 'Collapse desk read' : 'Fresh desk read — expand'}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedReadId((prev) => (prev === asset.id ? null : asset.id));
                                }}
                                className="flex-shrink-0 px-0.5 font-mono text-[10px] leading-none text-amber-500 dark:text-amber-400 hover:scale-125 transition-transform"
                              >
                                ◆
                              </button>
                            )}
                          </div>
                          <div className="text-xs font-mono text-neutral-500 truncate">
                            {asset.type === 'token' ? asset.name : asset.slug}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="font-mono font-bold text-sm text-neutral-900 dark:text-white truncate">
                        {asset.priceFormatted}
                      </div>
                      {asset.type === 'nft' && (
                        <div className="text-[10px] font-mono text-neutral-400 dark:text-neutral-600">floor</div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {asset.hasChangeData ? (
                        <span className={`font-mono font-bold text-sm ${
                          asset.change24h >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
                        }`}>
                          {asset.change24h >= 0 ? '↑' : '↓'}{Math.abs(asset.change24h).toFixed(1)}%
                        </span>
                      ) : (
                        <span className="text-neutral-400 dark:text-neutral-600 font-mono text-sm">—</span>
                      )}
                    </td>
                    {showSpark && (
                      <td className="px-3 py-3 text-center">
                        {asset.type === 'token' && asset.symbol ? (
                          (() => {
                            const historyData = priceHistoryMap.get(asset.symbol.toUpperCase());
                            return (
                              <TokenSparklineCompact
                                symbol={asset.symbol}
                                prices={historyData?.prices}
                                isUp={historyData?.isUp}
                              />
                            );
                          })()
                        ) : (
                          <span className="text-neutral-400 dark:text-neutral-600 font-mono text-xs">—</span>
                        )}
                      </td>
                    )}
                    {showVol && (
                      <td className="px-3 py-3 text-right">
                        <div className="font-mono font-medium text-sm text-neutral-500 dark:text-neutral-400 truncate">
                          {asset.volumeFormatted}
                        </div>
                      </td>
                    )}
                    {showMcap && (
                      <td className="px-3 py-3 text-right">
                        {asset.type === 'token' ? (
                          <div className="font-mono font-medium text-sm text-neutral-500 dark:text-neutral-400 truncate">
                            {asset.marketCapFormatted || '—'}
                          </div>
                        ) : (
                          <div className="font-mono font-medium text-sm text-neutral-500 dark:text-neutral-400 truncate" title="Total sales count">
                            {asset.sales?.toLocaleString() || '—'}
                          </div>
                        )}
                      </td>
                    )}
                  </tr>

                  {/* ◆ expanded: the desk's read INLINE under the row (market-v2 —
                      the old per-asset recon strip dissolved into the tape). */}
                  {isReadExpanded && read && (
                    <tr className="bg-neutral-50 dark:bg-neutral-900/70">
                      <td colSpan={visibleCols} className="px-4 py-3 text-left" style={{ boxShadow: 'inset 2px 0 0 #E8A832' }}>
                        <p className="font-mono text-[11px] leading-relaxed text-neutral-600 dark:text-neutral-300 whitespace-normal">
                          {read.analysis}
                        </p>
                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[8.5px] uppercase tracking-[0.06em] text-neutral-500 dark:text-neutral-500">
                          <span>Desk read · {getTimeAgo(read.timestamp)}</span>
                          {read.chips.map((c) => (
                            <span key={c} className="border-[0.5px] border-neutral-200 dark:border-neutral-700 px-1.5 py-px">
                              {c}
                            </span>
                          ))}
                          {read.entitySlug && (
                            <Link
                              href={entityUrl(read.entitySlug)}
                              onClick={(e) => e.stopPropagation()}
                              className="font-semibold text-amber-600 dark:text-amber-400 hover:underline underline-offset-2"
                            >
                              {read.entityName || (asset.type === 'token' ? asset.symbol : asset.name)} dossier →
                            </Link>
                          )}
                          {tick && (
                            <Link
                              href={tick.href}
                              onClick={(e) => e.stopPropagation()}
                              title={tick.label}
                              className="font-semibold text-amber-600 dark:text-amber-400 hover:underline underline-offset-2"
                            >
                              {tick.kind === 'wire' ? 'On the wire →' : 'In the latest brief →'}
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {filteredAssets.length === 0 && (
        <div className="py-12 text-center bg-white dark:bg-neutral-950 flex flex-col items-center">
          <StateMark size={88} className="mb-4 opacity-80" />
          <div className="text-neutral-500 dark:text-neutral-500 font-mono text-xs mb-3">NO ASSETS FOUND</div>
          <button
            onClick={() => {
              setSearchTerm('');
              setShowFeaturedOnly(false);
            }}
            className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-white font-mono text-xs font-semibold px-3 py-1.5 border border-emerald-300 dark:border-emerald-700/30 rounded hover:bg-emerald-50 dark:hover:bg-emerald-900/10 transition-colors"
          >
            CLEAR FILTERS
          </button>
        </div>
      )}
    </div>
  );
}
